import { EventHandler } from '@paradigm/utils/src/createObservable';
import { RfqId } from '#/unified-rfqs/entities/rfq';
import {
  GrfqQuote,
  GrfqQuotes,
  isQuoteNewer,
} from '#/unified-rfqs/entities/grfq/domain';

import GrfqAsksBidsStore from '#/unified-rfqs/store/grfq-asks-bids';

export type QuoteId = GrfqQuote['id'];

interface RfqIdQuotes extends GrfqQuotes {
  id: RfqId;
}

export default class GrfqQuotesStore {
  private rfqQuotes = new Map<RfqId, GrfqAsksBidsStore>([]);
  private removedQuotes = new Map<QuoteId, number>();
  private lastCleanup = 0;
  private static REMOVED_TTL_MS = 60 * 1000; // 1 minute
  private static CLEANUP_INTERVAL_MS = 1000; // 1 second

  private getRfqQuotesStore(rfqId: RfqId) {
    let rfqQuotesStore = this.rfqQuotes.get(rfqId);

    if (rfqQuotesStore == null) {
      rfqQuotesStore = new GrfqAsksBidsStore();
      this.rfqQuotes.set(rfqId, rfqQuotesStore);
    }

    return rfqQuotesStore;
  }

  private isQuoteValidForUpdate = (quote: GrfqQuote) => {
    this.maybeCleanupRemovedQuotes();
    if (this.removedQuotes.has(quote.id)) return false;
    const currentQuote = this.getRfqQuotesStore(quote.rfq_id).selectQuoteById(
      quote.id,
    );
    if (currentQuote == null) return true;
    return isQuoteNewer(currentQuote, quote);
  };

  selectRfqQuotes(rfqId: RfqId) {
    return this.getRfqQuotesStore(rfqId).getRfqQuotes();
  }

  selectRfqQuoteById(rfqId: RfqId, quoteId: GrfqQuote['id']) {
    return this.getRfqQuotesStore(rfqId).selectQuoteById(quoteId);
  }

  upsertSideQuotes({ id: rfqId, bids, asks }: RfqIdQuotes) {
    // Filter out quotes that were previously removed
    const filteredBids = bids.filter(this.isQuoteValidForUpdate);
    const filteredAsks = asks.filter(this.isQuoteValidForUpdate);

    this.getRfqQuotesStore(rfqId).upsertQuotes({
      bids: filteredBids,
      asks: filteredAsks,
    });
  }

  upsertQuote(quote: GrfqQuote) {
    // Remove quote if it was previously removed
    if (!this.isQuoteValidForUpdate(quote)) return;

    const rfqId = quote.rfq_id;
    const rfqIdQuotes = this.getRfqQuotesStore(rfqId);

    if (quote.side === 'BUY') {
      rfqIdQuotes.upsertQuotes({ bids: [quote], asks: [] });
      return;
    }

    rfqIdQuotes.upsertQuotes({ bids: [], asks: [quote] });
  }

  upsertQuotes(quotes: readonly GrfqQuote[]) {
    quotes.forEach((quote) => this.upsertQuote(quote));
  }

  removeQuote(quote: GrfqQuote) {
    this.removedQuotes.set(quote.id, Date.now());
    const rfqId = quote.rfq_id;
    this.getRfqQuotesStore(rfqId).removeQuote(quote);
  }

  updateQuote(quote: GrfqQuote) {
    const rfqId = quote.rfq_id;

    this.getRfqQuotesStore(rfqId).updateQuote(quote);
  }

  subscribeRfqQuotes(rfqId: RfqId, handler: EventHandler<GrfqQuotes>) {
    return this.getRfqQuotesStore(rfqId).subscribeQuotes(handler);
  }

  clear() {
    this.removedQuotes.clear();
    this.rfqQuotes.forEach((store) => store.clear());
  }

  clearRfqQuotes(rfqId: RfqId) {
    this.getRfqQuotesStore(rfqId).clear();
  }

  private maybeCleanupRemovedQuotes() {
    const now = Date.now();
    if (now - this.lastCleanup > GrfqQuotesStore.CLEANUP_INTERVAL_MS) {
      this.cleanupRemovedQuotes();
      this.lastCleanup = now;
    }
  }

  private cleanupRemovedQuotes() {
    const now = Date.now();
    for (const [id, ts] of this.removedQuotes) {
      if (now - ts > GrfqQuotesStore.REMOVED_TTL_MS) {
        this.removedQuotes.delete(id);
      }
    }
  }
}
